import {
  apiService,
  CreateDisputeQuery,
  DeviceDetails,
  GetChargebackInfoRequest,
  GetChargebackInfoResponse,
  InitiateLendingPayoutQuery,
  ToggleAutopayRequest,
  ToggleAutopayResponse,
} from '../../..'
import {
  ConfirmCustomPaymentPayNowQuery,
  InitiateCustomAmountPaymentQuery,
  InitiateCustomAmountPaymentResponse,
  OrderResponse,
  OrdersResponse,
  OrderPpaResponse,
  OrderPpaDocumentResponse,
  DisputeInfoResponse,
  GetDisputeInfoRequest,
  NotifyReturnResponse,
} from '../types'
import { OrderBy, ConfirmPayNowResponse } from '../../paymentSchedule'
import { stringify } from 'qs'
import { isResolvedChargeback } from '../utils'

export interface GetOrderTransactionsQuery {
  offset?: number
  limit?: number
  orderBy?: OrderBy
  ascending?: boolean
  payMonthly?: boolean
  payInFour?: boolean
  active?: boolean
  inactive?: boolean
}

export const defaultOrdersParams: GetOrderTransactionsQuery = {
  offset: 0,
  limit: 10,
  orderBy: OrderBy.ORDER_DATE,
  ascending: false,
  payMonthly: false,
  payInFour: false,
  active: false,
  inactive: false,
}

interface ChangeOrderTansactionPaymentMethodRequest {
  paymentSourceId?: string
  cardId?: string | number
  bankAccountId?: string
  cashAppPayId?: number
  deviceDetails?: DeviceDetails
}
export interface ChangeOrderTransactionPaymentMethodQuery {
  orderId: number | string
  body: ChangeOrderTansactionPaymentMethodRequest
}

const baseURL = '/portal/consumers/ordertransactions'

export const orderTransactionsService = apiService.service.injectEndpoints({
  endpoints: (build) => ({
    getOrderTransactions: build.query<OrdersResponse, GetOrderTransactionsQuery>({
      query: (params) => {
        const stringifiedParams = stringify({ ...defaultOrdersParams, ...params })
        return {
          url: `${baseURL}?${stringifiedParams}`,
          method: 'GET',
        }
      },
      providesTags: (result) =>
        result !== undefined && result !== null
          ? [...result.results.map((order) => ({ type: 'OrderTransactions' as const, id: order.id })), 'OrderTransactions']
          : ['OrderTransactions'],
    }),
    getOrderTransaction: build.query<OrderResponse, string>({
      query: (id) => ({ url: `${baseURL}/${id}`, method: 'GET' }),
      providesTags: (result) => (result !== undefined && result !== null ? [{ type: 'OrderTransactions' as const, id: result.id }] : ['OrderTransactions']),
    }),
    changeOrderTransactionPaymentMethod: build.mutation<void, ChangeOrderTransactionPaymentMethodQuery>({
      query: ({ orderId, body }) => ({
        url: `${baseURL}/${orderId}/payment-method`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_, __, request) => [{ type: 'OrderTransactions', id: request.orderId }, 'BankAccounts', 'CreditCards'],
    }),
    getOrderTransactionPpa: build.query<OrderPpaResponse, string>({
      query: (id) => ({ url: `/portal/consumers/ordertransactions/${id}/ppa`, method: 'GET' }),
      providesTags: ['OrderTransactionPpa'],
    }),
    getOrderTransactionPpaDocument: build.query<OrderPpaDocumentResponse, string>({
      query: (documentId) => ({ url: `/portal/consumers/ppa/${documentId}`, method: 'GET' }),
    }),
    initiateCustomAmountPayment: build.mutation<InitiateCustomAmountPaymentResponse, InitiateCustomAmountPaymentQuery>({
      query: ({ orderId, body }) => ({
        url: `${baseURL}/${orderId}/paynow/initiate`,
        method: 'POST',
        data: body,
      }),
    }),
    initiateLendingPayout: build.mutation<InitiateCustomAmountPaymentResponse, InitiateLendingPayoutQuery>({
      query: ({ orderId }) => ({
        url: `${baseURL}/${orderId}/paynow/lending/initiate`,
        method: 'POST',
      }),
    }),
    initiateLendingCustomAmountPayment: build.mutation<InitiateCustomAmountPaymentResponse, InitiateCustomAmountPaymentQuery>({
      query: ({ orderId, body }) => ({
        url: `${baseURL}/${orderId}/custom-payment/lending/init`,
        method: 'POST',
        data: body,
      }),
    }),
    confirmCustomAmountPayNow: build.mutation<ConfirmPayNowResponse, ConfirmCustomPaymentPayNowQuery>({
      query: ({ orderId, body }) => ({
        url: `${baseURL}/${orderId}/paynow/confirm`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_, __, request) => [{ type: 'OrderTransactions', id: request.orderId }, 'AmountDueSummary', 'CreditCards', 'CreditLimit'],
    }),
    confirmLendingPayout: build.mutation<ConfirmPayNowResponse, ConfirmCustomPaymentPayNowQuery>({
      query: ({ orderId, body }) => ({
        url: `${baseURL}/${orderId}/paynow/lending/confirm`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_, __, request) => [{ type: 'OrderTransactions', id: request.orderId }, 'AmountDueSummary', 'PaymentSchedule', 'CreditFacilitySummary'],
    }),
    confirmLendingCustomAmountPayment: build.mutation<ConfirmPayNowResponse, ConfirmCustomPaymentPayNowQuery>({
      query: ({ orderId, body }) => ({
        url: `${baseURL}/${orderId}/custom-payment/lending/confirm`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_, __, request) => [
        { type: 'OrderTransactions', id: request.orderId },
        'AmountDueSummary',
        'CreditCards',
        'CreditLimit',
        'CreditFacilitySummary',
      ],
    }),
    createDispute: build.mutation<null, CreateDisputeQuery>({
      query: ({ orderId, formData }) => ({
        url: `${baseURL}/${orderId}/create-dispute`,
        method: 'POST',
        data: formData,
      }),
    }),
    notifyReturn: build.mutation<null, NotifyReturnResponse>({
      query: ({ orderId, body }) => ({
        url: `${baseURL}/${orderId}/notify-return`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, request) => [{ type: 'OrderTransactions', id: request.orderId }],
    }),
    toggleAutopay: build.mutation<ToggleAutopayResponse, ToggleAutopayRequest>({
      query: ({ orderId, enabled }) => ({
        url: `${baseURL}/${orderId}/autopay`,
        method: 'POST',
        data: { enabled },
      }),
      invalidatesTags: (_, __, request) => [{ type: 'OrderTransactions', id: request.orderId }, 'OrderTransactions', 'OrderTransactionPpa'],
    }),
    getDisputeInfo: build.mutation<DisputeInfoResponse, GetDisputeInfoRequest>({
      query: ({ orderId, type }) => ({
        url: `${baseURL}/${orderId}/dispute?type=${type}`,
        method: 'GET',
      }),
    }),
    getChargebackInfo: build.mutation<GetChargebackInfoResponse, GetChargebackInfoRequest>({
      query: ({ orderId, limit = 20, offset = 0 }) => ({
        url: `${baseURL}/${orderId}/chargeback?limit=${limit}&orderId=${orderId}&offset=${offset}`,
        method: 'GET',
      }),
      transformResponse: (response: GetChargebackInfoResponse) => {
        return {
          ...response,
          results: (response?.results ?? []).map((chargeback) => {
            return {
              ...chargeback,
              isResolved: isResolvedChargeback(chargeback),
            }
          }),
        }
      },
    }),
  }),
})
